import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  boardVotingEnabledPatchSchema,
  boardVotingEnabledSchema,
  boardVotingHideTalliesPatchSchema,
  boardVotingHideTalliesSchema,
  boardVotingPermissionPatchSchema,
  boardVotingPermissionSchema,
  cardVoteStateSchema,
  cardVoteTallySchema,
  type ApiError,
  type BoardContents,
  type BoardVotingEnabled,
  type BoardVotingEnabledPatch,
  type BoardVotingHideTallies,
  type BoardVotingHideTalliesPatch,
  type BoardVotingPermission,
  type BoardVotingPermissionPatch,
  type CardVoteState,
  type CardVoteTally,
} from '@molt/shared';

import { del, get, patch, put } from './api';
import { setBoardContentsQueries } from './cross-board-move';
import { queryKeys } from './query-keys';

export interface CardVoteVariables {
  readonly boardId: string;
  readonly cardId: string;
}

export function getCardVoteState(cardId: string): Promise<CardVoteState> {
  return get(`/cards/${encodeURIComponent(cardId)}/vote`, cardVoteStateSchema);
}

export function voteForCard(cardId: string): Promise<CardVoteState> {
  return put(`/cards/${encodeURIComponent(cardId)}/vote`, cardVoteStateSchema);
}

export function unvoteForCard(cardId: string): Promise<CardVoteState> {
  return del(`/cards/${encodeURIComponent(cardId)}/vote`, cardVoteStateSchema);
}

export function getCardVoteTally(cardId: string): Promise<CardVoteTally> {
  return get(`/cards/${encodeURIComponent(cardId)}/votes`, cardVoteTallySchema);
}

export function getBoardVotingEnabled(boardId: string): Promise<BoardVotingEnabled> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/voting-enabled`,
    boardVotingEnabledSchema,
  );
}

export function updateBoardVotingEnabled(
  boardId: string,
  patchBody: BoardVotingEnabledPatch,
): Promise<BoardVotingEnabled> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/voting-enabled`,
    boardVotingEnabledSchema,
    boardVotingEnabledPatchSchema.parse(patchBody),
  );
}

export function getBoardVotingPermission(
  boardId: string,
): Promise<BoardVotingPermission> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/voting-permission`,
    boardVotingPermissionSchema,
  );
}

export function updateBoardVotingPermission(
  boardId: string,
  patchBody: BoardVotingPermissionPatch,
): Promise<BoardVotingPermission> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/voting-permission`,
    boardVotingPermissionSchema,
    boardVotingPermissionPatchSchema.parse(patchBody),
  );
}

export function getBoardVotingHideTallies(
  boardId: string,
): Promise<BoardVotingHideTallies> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/voting-hide-tallies`,
    boardVotingHideTalliesSchema,
  );
}

export function updateBoardVotingHideTallies(
  boardId: string,
  patchBody: BoardVotingHideTalliesPatch,
): Promise<BoardVotingHideTallies> {
  return patch(
    `/boards/${encodeURIComponent(boardId)}/voting-hide-tallies`,
    boardVotingHideTalliesSchema,
    boardVotingHideTalliesPatchSchema.parse(patchBody),
  );
}

export function applyCardVoteStateToBoardContents(
  previous: BoardContents | undefined,
  cardId: string,
  state: CardVoteState,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  let changed = false;
  const lists = previous.lists.map((list) => {
    let listChanged = false;
    const cards = list.cards.map((card) => {
      if (card.id !== cardId) return card;
      if (card.voteCount === state.voteCount) return card;
      changed = true;
      listChanged = true;
      return { ...card, voteCount: state.voteCount };
    });

    return listChanged ? { ...list, cards } : list;
  });

  return changed ? { ...previous, lists } : previous;
}

export function cardVoteStateQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.vote(cardId),
    queryFn: () => getCardVoteState(cardId),
  });
}

export function cardVoteTallyQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.votes(cardId),
    queryFn: () => getCardVoteTally(cardId),
  });
}

export function boardVotingEnabledQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.votingEnabled(boardId),
    queryFn: () => getBoardVotingEnabled(boardId),
  });
}

export function boardVotingPermissionQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.votingPermission(boardId),
    queryFn: () => getBoardVotingPermission(boardId),
  });
}

export function boardVotingHideTalliesQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.votingHideTallies(boardId),
    queryFn: () => getBoardVotingHideTallies(boardId),
  });
}

export function useCardVoteState(cardId: string, enabled = true) {
  return useQuery({ ...cardVoteStateQueryOptions(cardId), enabled });
}

export function useCardVoteTally(cardId: string, enabled = true) {
  return useQuery({
    ...cardVoteTallyQueryOptions(cardId),
    enabled,
  });
}

export function useVoteForCard(): UseMutationResult<
  CardVoteState,
  ApiError,
  CardVoteVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CardVoteState, ApiError, CardVoteVariables>({
    mutationFn: (variables) => voteForCard(variables.cardId),
    onSuccess: (state, variables) => {
      applyVoteState(queryClient, variables, state);
    },
  });
}

export function useUnvoteForCard(): UseMutationResult<
  CardVoteState,
  ApiError,
  CardVoteVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CardVoteState, ApiError, CardVoteVariables>({
    mutationFn: (variables) => unvoteForCard(variables.cardId),
    onSuccess: (state, variables) => {
      applyVoteState(queryClient, variables, state);
    },
  });
}

function applyVoteState(
  queryClient: QueryClient,
  variables: CardVoteVariables,
  state: CardVoteState,
): void {
  queryClient.setQueryData(queryKeys.cards.vote(state.cardId), state);
  setBoardContentsQueries(queryClient, variables.boardId, (previous) =>
    applyCardVoteStateToBoardContents(previous, state.cardId, state),
  );

  void queryClient.invalidateQueries({
    queryKey: queryKeys.cards.vote(state.cardId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.cards.votes(state.cardId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.cards.detail(state.cardId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.detail(variables.boardId),
  });
}

export function useBoardVotingEnabled(boardId: string, enabled = true) {
  return useQuery({
    ...boardVotingEnabledQueryOptions(boardId),
    enabled,
  });
}

export function useBoardVotingPermission(boardId: string, enabled = true) {
  return useQuery({
    ...boardVotingPermissionQueryOptions(boardId),
    enabled,
  });
}

export function useBoardVotingHideTallies(boardId: string, enabled = true) {
  return useQuery({
    ...boardVotingHideTalliesQueryOptions(boardId),
    enabled,
  });
}

export function useUpdateBoardVotingEnabled(
  boardId: string,
): UseMutationResult<BoardVotingEnabled, ApiError, BoardVotingEnabledPatch> {
  const queryClient = useQueryClient();
  return useMutation<BoardVotingEnabled, ApiError, BoardVotingEnabledPatch>({
    mutationFn: (body) => updateBoardVotingEnabled(boardId, body),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.boards.votingEnabled(boardId), state);
      invalidateBoardVotingReaders(
        queryClient,
        boardId,
        queryKeys.boards.votingEnabled(boardId),
      );
    },
  });
}

export function useUpdateBoardVotingPermission(
  boardId: string,
): UseMutationResult<BoardVotingPermission, ApiError, BoardVotingPermissionPatch> {
  const queryClient = useQueryClient();
  return useMutation<BoardVotingPermission, ApiError, BoardVotingPermissionPatch>({
    mutationFn: (body) => updateBoardVotingPermission(boardId, body),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.boards.votingPermission(boardId), state);
      invalidateBoardVotingReaders(
        queryClient,
        boardId,
        queryKeys.boards.votingPermission(boardId),
      );
    },
  });
}

export function useUpdateBoardVotingHideTallies(
  boardId: string,
): UseMutationResult<BoardVotingHideTallies, ApiError, BoardVotingHideTalliesPatch> {
  const queryClient = useQueryClient();
  return useMutation<BoardVotingHideTallies, ApiError, BoardVotingHideTalliesPatch>({
    mutationFn: (body) => updateBoardVotingHideTallies(boardId, body),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.boards.votingHideTallies(boardId), state);
      invalidateBoardVotingReaders(
        queryClient,
        boardId,
        queryKeys.boards.votingHideTallies(boardId),
      );
    },
  });
}

function invalidateBoardVotingReaders(
  queryClient: QueryClient,
  boardId: string,
  settingKey: readonly unknown[],
): void {
  void queryClient.invalidateQueries({ queryKey: settingKey });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.contents(boardId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
}
